import React, { ReactNode, useLayoutEffect, useRef, useState } from 'react'
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  TouchableWithoutFeedback,
  Pressable,
  Animated,
  ViewStyle,
  StyleProp,
  TextStyle,
  Platform,
} from 'react-native'
import MaterialIcon from 'react-native-vector-icons/MaterialIcons'
import Toast from 'APP/Components/Toast'
import { useOnLayout } from 'APP/Hooks/Layout'
import styled, { useTheme } from 'styled-components/native'
import { ApplicationStyles } from 'APP/Themes'
import Fonts from 'APP/Themes/Fonts'
import { KeyboardAvoidingView } from 'react-native-keyboard-controller'
import i18n from 'APP/I18n'

interface BottomSheetModalProps {
  isVisible: boolean
  goBack?: () => void
  onClose: () => void
  title?: ReactNode
  subtitle?: string
  children: ReactNode
  testID?: string
  style?: StyleProp<ViewStyle>
  containerStyle?: StyleProp<ViewStyle>
  titleStyle?: StyleProp<TextStyle>
  headerStyle?: StyleProp<ViewStyle>
  hideHeader?: boolean
}

const StyledKeyboardAvoidingView = styled(KeyboardAvoidingView)({
  flex: 1,
})

const Overlay = styled(Pressable)({
  flex: 1,
  backgroundColor: 'rgba(0,0,0,0.5)',
  justifyContent: 'flex-end',
  ...(Platform.OS === 'web' && ({ cursor: 'default' } as const)),
})

const ModalContainer = styled(View)`
  ${ApplicationStyles.shadowHeavy};
  padding-top: ${({ theme }) => theme.metrics.baseMargin * 2}px;
  background-color: ${({ theme }) => theme.colors.background};
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
`

const Header = styled(View)`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding-left: ${({ theme }) => theme.metrics.baseMargin * 1.8}px;
  padding-right: ${({ theme }) => theme.metrics.baseMargin * 2}px;
`

const GoBackCta = styled(TouchableOpacity)`
  margin-right: ${({ theme }) => theme.metrics.baseMargin * 0.3}px;
  margin-top: 5px;
`

const HeaderText = styled(View)({
  flex: 1,
})

const Title = styled(Text)`
  ${({ theme }) => ({ ...Fonts.style.h2, color: theme.colors.text })}
  padding-bottom: ${({ theme }) => theme.metrics.baseMargin}px;
  padding-right: ${({ theme }) => theme.metrics.baseMargin * 1.25}px;
`

const Subtitle = styled(Text)`
  ${({ theme }) => ({ ...Fonts.style.caption, color: theme.colors.text })}
  text-transform: uppercase;
`

const BottomSheetModal = ({
  isVisible: isVisibleProp,
  goBack,
  onClose,
  title,
  subtitle,
  children,
  testID,
  style,
  containerStyle = {},
  titleStyle = {},
  headerStyle = {},
  hideHeader = false,
}: BottomSheetModalProps) => {
  const slideIn = useRef(new Animated.Value(0)).current
  const theme = useTheme()

  const animate = (toValue: number, cb?: () => void) =>
    Animated.spring(slideIn, {
      toValue,
      useNativeDriver: true,
      overshootClamping: true,
    }).start(() => {
      if (typeof cb === 'function') cb()
    })

  const [isVisible, setIsVisible] = useState(false)

  useLayoutEffect(() => {
    if (isVisibleProp) {
      setIsVisible(true)
      animate(1)
    } else {
      animate(0, () => setIsVisible(false))
    }
  }, [isVisibleProp])
  const {
    layout: { height: modalHeight },
    onLayout: onModalLayout,
  } = useOnLayout()

  return (
    <Modal
      statusBarTranslucent
      animationType="fade"
      transparent
      visible={isVisible}
      onRequestClose={onClose}
      testID={testID}
    >
      <StyledKeyboardAvoidingView behavior="padding" keyboardVerticalOffset={0}>
        <Overlay onPress={onClose}>
          <TouchableWithoutFeedback>
            <Animated.View
              style={{
                transform: [
                  {
                    translateY: slideIn.interpolate({
                      inputRange: [0, 1],
                      outputRange: [modalHeight, 0],
                    }),
                  },
                ],
                ...(containerStyle as object),
              }}
            >
              <ModalContainer style={style} onLayout={onModalLayout}>
                {!hideHeader && (
                  <Header style={headerStyle}>
                    {goBack && (
                      <GoBackCta
                        onPress={goBack}
                        testID="modal.goBack"
                        accessibilityLabel={i18n.t('AccessibilityIcons.back')}
                      >
                        <MaterialIcon
                          name="arrow-back-ios"
                          size={theme.metrics.icon.small}
                          color={theme.colors.text}
                        />
                      </GoBackCta>
                    )}
                    <HeaderText>
                      {subtitle && <Subtitle>{subtitle}</Subtitle>}
                      <Title style={titleStyle}>{title}</Title>
                    </HeaderText>
                    {!goBack && (
                      <TouchableOpacity onPress={onClose} testID="modal.close">
                        <MaterialIcon
                          name="close"
                          size={theme.metrics.icon.small}
                          color={theme.colors.text}
                        />
                      </TouchableOpacity>
                    )}
                  </Header>
                )}
                {children}
              </ModalContainer>
            </Animated.View>
          </TouchableWithoutFeedback>
        </Overlay>
      </StyledKeyboardAvoidingView>

      <Toast darkBackground />
    </Modal>
  )
}

export default BottomSheetModal
